import Loop from './Loop';
import caseStudiesData from '../data/caseStudies.json';

/**
 * Case studies. Reads from src/data/caseStudies.json so new entries never
 * touch this component.
 *
 * Rules that must not be broken:
 *  - Renders nothing while both lists are empty.
 *  - Never a company name, for either side of an introduction. SECTOR only.
 *  - Cards show the trigger only. Title and metric carry the outcome; match
 *    and outcome stay in the JSON for reference.
 *  - Introductions and outbound work stay in separate lists. The page sells
 *    introductions, not booked meetings, so outbound results are labelled as
 *    a different model and never mixed in with the introductions.
 */
export type IntroductionCase = {
  sector: string;
  metric: string;
  title: string;
  trigger: string;
  match: string;
  outcome: string;
};

export type OutboundCase = {
  sector: string;
  metric: string;
  summary: string;
};

type CaseStudiesFile = {
  introductions: IntroductionCase[];
  outbound: OutboundCase[];
};

const data = caseStudiesData as CaseStudiesFile;
const introductions = data.introductions ?? [];
const outbound = data.outbound ?? [];

export const hasCaseStudies = introductions.length + outbound.length > 0;

export default function CaseStudies() {
  if (!hasCaseStudies) return null;

  return (
    <section id="case-studies" className="bg-black pt-8">
      <div className="container-x">
        <div className="intro-block grid gap-8 border-t border-white/10 pt-16 md:grid-cols-[180px_1fr] md:gap-16">
          <div>
            <p className="font-mono text-xs uppercase tracking-label text-gold">
              Case studies
            </p>
            <p className="mt-2 font-mono text-xs tracking-label text-stone">
              Sector only
            </p>
          </div>
          <div>
            <h2 className="font-display text-section font-light text-white text-balance">
              Introductions already made.
            </h2>
            <p className="mt-6 max-w-2xl font-sans text-body text-white/85">
              What moved, who it was routed to, and what happened next. Company
              names on both sides are held back. The sector, the sequence and the
              numbers are as recorded.
            </p>
          </div>
        </div>
      </div>

      {introductions.length > 0 && (
        <Loop seconds={introductions.length * 11} label="Routed introductions">
          {(copy) =>
            introductions.map((study, index) => (
              <IntroductionCard
                key={`${copy}-${study.title}-${index}`}
                study={study}
                testId={copy === 0 ? 'case-study' : undefined}
              />
            ))
          }
        </Loop>
      )}

      {outbound.length > 0 && (
        <>
          <div className="container-x">
            <div className="mt-16 md:ml-[calc(180px+4rem)]">
              <h3 className="font-display text-2xl font-light text-white">
                Before introductions: outbound work
              </h3>
              <p className="mt-4 max-w-2xl font-sans text-body text-white/85">
                Earlier engagements ran on outbound systems rather than routed
                introductions. A different model, listed separately so the two are
                never confused. The same discipline about who is worth a
                conversation runs through both.
              </p>
            </div>
          </div>
          <Loop seconds={outbound.length * 8} reverse label="Outbound work">
            {(copy) =>
              outbound.map((item, index) => (
                <div
                  key={`${copy}-${item.sector}-${index}`}
                  data-testid={copy === 0 ? 'outbound-case' : undefined}
                  className="mr-5 flex w-72 flex-none flex-col rounded-2xl border border-white/10 bg-charcoal p-6"
                >
                  <p className="min-h-[2lh] font-mono text-[0.65rem] uppercase tracking-label text-gold">
                    {item.sector}
                  </p>
                  <p className="mt-3 font-display text-xl font-light text-white">
                    {item.metric}
                  </p>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-white/75">
                    {item.summary}
                  </p>
                </div>
              ))
            }
          </Loop>
        </>
      )}
    </section>
  );
}

function IntroductionCard({
  study,
  testId,
}: {
  study: IntroductionCase;
  testId?: string;
}) {
  return (
    <article
      data-testid={testId}
      className="grain mr-5 flex w-[19rem] flex-none flex-col overflow-hidden rounded-4xl border border-white/10 bg-charcoal p-7 sm:w-[26rem] sm:p-8"
    >
      <p className="min-h-[2lh] font-mono text-[0.65rem] uppercase tracking-label text-gold">
        {study.sector}
      </p>
      <h3 className="mt-4 min-h-[4lh] font-display text-2xl font-light text-white text-balance sm:min-h-[2lh]">
        {study.title}
      </h3>
      <p className="mt-3 font-mono text-[0.65rem] uppercase tracking-label text-stone">
        {study.metric}
      </p>

      <div className="mt-6 border-t border-white/10 pt-4">
        <p className="font-mono text-[0.65rem] uppercase tracking-label text-stone">
          The trigger
        </p>
        <p className="mt-2 font-sans text-sm leading-relaxed text-white/85">
          {study.trigger}
        </p>
      </div>
    </article>
  );
}
